/**
 * The Dance World app's own colours, in both of the grounds it can be painted on.
 *
 * These are drawn from the app's `theme/variables.scss` rather than from this portal's palette,
 * because everything in `illustrations/` is a picture OF another product. A Foyer preview rendered
 * in portal grey would be showing an owner something they will never see, and the whole point of an
 * illustration here is that it is trustworthy enough to make a decision from.
 *
 * <b>Keep these in step with the app.</b> They are the app's `$paper` / `$ink` / `$surface` and the
 * two palette entries its dark theme changes. If a value here drifts, this page starts quietly
 * lying — which is worse than having no preview at all.
 */
export interface IAppPalette {
  /** The page behind everything. The app's `$paper`. */
  page: string;
  /** Body text and headings. The app's `$ink`. */
  ink: string;
  /** What a card, list group or toolbar is printed on. The app's `$surface`. */
  surface: string;
  /** Secondary text — captions, helper lines. The app's `medium`. */
  medium: string;
  /** Teal as a GLYPH colour. Deliberately not the same in both: brand fills never move, ink does. */
  accent: string;
  /** Hairlines and dividers, as an rgba() the ground can show through. */
  hairline: string;
}

export const APP_PALETTE_LIGHT: IAppPalette = {
  page: '#FBF6F1',
  ink: '#2A2521',
  surface: '#FFFFFF',
  medium: '#6E6055',
  accent: '#008080',
  hairline: 'rgba(42, 37, 33, 0.10)'
};

export const APP_PALETTE_DARK: IAppPalette = {
  page: '#161311',
  /* The dark ink IS the light theme's paper, unchanged — that is how the app is written. */
  ink: '#FBF6F1',
  surface: '#231F1C',
  medium: '#ACA097',
  /*
    Teal lifted for a dark ground. A fill and a label of the same hue are held to different
    standards (3:1 against 4.5:1), so the app keeps #008080 as a FILL in both themes and brightens
    it only where it is read as text or a glyph — which is everything drawn in these previews.
  */
  accent: '#5FC9C2',
  hairline: 'rgba(251, 246, 241, 0.16)'
};

/**
 * A notice's own paper, which does <b>not</b> follow the theme.
 *
 * A studio picks its notice colours in the portal and the API stores them as resolved values, so a
 * butter note is butter on both grounds — that is exactly the trade-off a school is weighing on the
 * Dancer App tab, and drawing it any other way would hide the decision being made.
 */
export const APP_NOTICE_BUTTER = '#FBEFC9';
export const APP_NOTICE_INK = '#2A2521';

/**
 * How far the half of a drawing that is NOT being edited recedes. Far enough to read as background,
 * not so far it vanishes.
 *
 * Shared because two illustrations now serve two settings each, and a preview where the emphasis is
 * a slightly different strength on each tab reads as a bug rather than as a system.
 */
export const RECEDED_OPACITY = 0.28;
